import {getHumanFeedbackMessage} from '../../utils/humanFeedback';
import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  AlertTriangle,
  CheckCircle2,
  FileCheck2,
  Loader2,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';

import { functions } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';

const TermoResponsabilidadeGate = ({ termoAtual }) => {
  const { user, setTermoResponsabilidadeAceito } = useAuth();
  const [term, setTerm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');

  const municipioId = user?.municipioId || '';
  const expectedVersionId = String(termoAtual?.versaoId || '');
  const expectedHash = String(termoAtual?.hashConteudo || '');

  const versionLabel = useMemo(
    () =>
      termoAtual?.versao
        ? `Versão ${termoAtual.versao}`
        : 'Versão vigente',
    [termoAtual?.versao]
  );

  const loadTerm = async () => {
    if (!municipioId) return;

    setLoading(true);
    setError('');
    setConfirmed(false);

    try {
      const callable = httpsCallable(
        functions,
        'obterTermoResponsabilidadeServidor'
      );
      const response = await callable({ municipioId });
      const loaded = response?.data?.termoAtual || null;

      if (!loaded?.versaoId || !loaded?.hashConteudo) {
        throw new Error(
          'O Município ainda não possui um termo publicado disponível.'
        );
      }

      setTerm(loaded);
    } catch (loadError) {
      console.error(
        'Erro ao carregar termo de responsabilidade:',
        loadError
      );
      setTerm(null);
      setError(
        loadError?.message ||
          'Não foi possível carregar o Termo de Responsabilidade. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTerm();
    // A mudança de versão vem do listener municipal já existente.
    // Nenhum listener adicional é criado nesta tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipioId, expectedVersionId, expectedHash]);

  const handleAccept = async () => {
    if (!confirmed || !term || accepting) return;

    setAccepting(true);
    setError('');

    try {
      const callable = httpsCallable(
        functions,
        'aceitarTermoResponsabilidadeServidor'
      );
      const response = await callable({
        municipioId,
        versaoId: term.versaoId,
        hashConteudo: term.hashConteudo,
        confirmou: true,
      });

      const aceite = response?.data?.aceite;

      if (!response?.data?.ok || !aceite?.versaoId) {
        throw new Error('O aceite não foi confirmado pelo servidor.');
      }

      setTermoResponsabilidadeAceito?.(aceite);
    } catch (acceptError) {
      console.error(
        'Erro ao registrar aceite do termo:',
        acceptError
      );
      setError(
        acceptError?.message ||
          'Não foi possível registrar o aceite. Leia novamente a versão vigente e tente outra vez.'
      );
      await loadTerm();
    } finally {
      setAccepting(false);
    }
  };

  return (
    <main className="min-h-screen bg-gray-50 px-4 py-6 dark:bg-gray-950 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        <div className="overflow-hidden rounded-[28px] border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <header className="border-b border-gray-100 px-5 py-5 dark:border-gray-800 sm:px-7">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">
                <ShieldCheck className="h-6 w-6" />
              </div>

              <div>
                <p className="text-xs font-black uppercase tracking-[0.16em] text-blue-600 dark:text-blue-300">
                  Aceite obrigatório
                </p>
                <h1 className="mt-1 text-xl font-black text-gray-950 dark:text-white sm:text-2xl">
                  Termo de Responsabilidade e Veracidade
                </h1>
                <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                  Antes de continuar no Portal do Servidor, leia o termo
                  vigente do seu Município e registre seu aceite.
                </p>
              </div>
            </div>
          </header>

          <div className="px-5 py-5 sm:px-7">
            {loading ? (
              <div className="flex min-h-[320px] items-center justify-center">
                <div className="text-center">
                  <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
                  <p className="mt-3 text-sm font-bold text-gray-500 dark:text-gray-400">
                    Carregando termo vigente...
                  </p>
                </div>
              </div>
            ) : error && !term ? (
              <div className="rounded-3xl border border-red-200 bg-red-50 p-5 dark:border-red-900 dark:bg-red-950/30">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-700 dark:text-red-300" />
                  <div>
                    <p className="font-black text-red-950 dark:text-red-100">
                      Não foi possível abrir o termo
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-red-800 dark:text-red-200">
                      {getHumanFeedbackMessage(error)}
                    </p>
                    <button
                      type="button"
                      onClick={loadTerm}
                      className="mt-4 inline-flex min-h-11 items-center justify-center rounded-2xl bg-white px-4 py-2.5 text-sm font-black text-red-700 ring-1 ring-red-200 hover:bg-red-50 dark:bg-gray-900 dark:text-red-200 dark:ring-red-800"
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Tentar novamente
                    </button>
                  </div>
                </div>
              </div>
            ) : term ? (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-gray-50 px-4 py-3 dark:bg-gray-950/60">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-400">
                      Documento vigente
                    </p>
                    <p className="mt-1 text-sm font-black text-gray-900 dark:text-white">
                      {versionLabel}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 text-xs font-bold text-green-700 dark:text-green-300">
                    <CheckCircle2 className="h-4 w-4" />
                    Versão publicada e validada
                  </div>
                </div>

                <article className="mt-4 max-h-[48vh] overflow-y-auto rounded-3xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-950/30 sm:p-6">
                  <h2 className="text-lg font-black text-gray-950 dark:text-white">
                    {term.titulo}
                  </h2>
                  <div className="mt-5 whitespace-pre-wrap text-sm leading-7 text-gray-700 dark:text-gray-300">
                    {term.conteudo}
                  </div>
                </article>

                <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/30">
                  <p className="text-xs leading-relaxed text-blue-900 dark:text-blue-100">
                    Sua responsabilidade limita-se aos dados, informações e
                    documentos que você fornecer, alterar, apresentar ou
                    confirmar diretamente. Informações previamente registradas
                    pela Administração Pública e que não tenham sido
                    modificadas ou confirmadas por você não são transferidas
                    para sua responsabilidade por este aceite.
                  </p>
                </div>

                {error && (
                  <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
                    {getHumanFeedbackMessage(error)}
                  </div>
                )}

                <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-2xl border border-gray-200 p-4 transition-colors hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/60">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(event) =>
                      setConfirmed(event.target.checked)
                    }
                    disabled={accepting}
                    className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                    <strong className="text-gray-950 dark:text-white">
                      Declaro que li e compreendi o termo acima.
                    </strong>{' '}
                    Confirmo meu aceite desta versão e estou ciente da
                    responsabilidade sobre as informações e documentos que
                    eu fornecer, alterar, apresentar ou confirmar no SISDP.
                  </span>
                </label>

                <button
                  type="button"
                  onClick={handleAccept}
                  disabled={!confirmed || accepting}
                  className="mt-5 flex min-h-12 w-full items-center justify-center rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {accepting ? (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  ) : (
                    <FileCheck2 className="mr-2 h-5 w-5" />
                  )}
                  {accepting
                    ? 'Registrando aceite...'
                    : 'Li, compreendi e aceito'}
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </main>
  );
};

export default TermoResponsabilidadeGate;
